import { Fragment, lazy, Suspense } from 'react'
import { Navigate, Route } from 'react-router'

import NavigationFooter from 'js/admin/Navigation/components/NavigationFooter'
import BlogAdd from 'js/blog/Add'
import BlogEdit from 'js/blog/Edit'
import BlogRoutes from 'js/blog/Routes'
import LoadingSpinner from 'js/components/LoadingSpinner/LoadingSpinner'
import VersionHistory from 'js/components/VersionHistory/VersionHistory'
import CoreRoutes from 'js/core/Routes'
import DiscussionsAdd from 'js/discussions/Add'
import DiscussionsEdit from 'js/discussions/Edit'
import DiscussionsRoutes from 'js/discussions/Routes'
import EventsAdd from 'js/events/Add'
import EventsEdit from 'js/events/Edit'
import EventGuestList from 'js/events/GuestList/GuestList'
import EventsRoutes from 'js/events/Routes'
import FileView from 'js/files/FileView'
import CustomContentCreate from 'js/genericArticle/Create'
import CustomContentDetail from 'js/genericArticle/Detail'
import CustomContentEdit from 'js/genericArticle/Edit'
import GroupAdd from 'js/group/Add'
import GroupEdit from 'js/group/Edit'
import Layout from 'js/layout/Layout'
import { getSafeCustomContentTypes } from 'js/lib/hooks/defaultSubtypes'
import MagazineAdd from 'js/magazine/AddEdit/Add'
import MagazineEdit from 'js/magazine/AddEdit/Edit/Edit'
import { MagazineRoutes, MagazinesRoutes } from 'js/magazine/Routes'
import MagazineIssueRoutes from 'js/magazine-issue/Routes'
import NewsAdd from 'js/news/Add'
import NewsEdit from 'js/news/Edit'
import NewsRoutes from 'js/news/Routes'
import PageAdd from 'js/page/Add'
import LegacyCmsPageRedirect from 'js/page/LegacyCmsPageRedirect'
import { PageRoutes } from 'js/page/Routes'
import { EpisodeAdd } from 'js/podcast/EpisodeAdd'
import { EpisodeEdit } from 'js/podcast/EpisodeEdit'
import { PodcastAdd } from 'js/podcast/PodcastAdd'
import { PodcastEdit } from 'js/podcast/PodcastEdit'
import PodcastRoutes from 'js/podcast/Routes'
import PollAdd from 'js/polls/Add'
import PollEdit from 'js/polls/Edit'
import PollsRoutes from 'js/polls/Routes'
import QuestionsAdd from 'js/questions/Add'
import QuestionsEdit from 'js/questions/Edit'
import QuestionsRoutes from 'js/questions/Routes'
import BaseLayout from 'js/router/BaseLayout'
import RequireAuth from 'js/router/RequireAuth'
import SearchRoutes from 'js/search/Routes'
import TaskAdd from 'js/tasks/Add'
import TaskEdit from 'js/tasks/Edit'
import UsersRoutes from 'js/users/Routes'
import WikiAdd from 'js/wiki/Add'
import WikiEdit from 'js/wiki/Edit'
import WikiRoutes from 'js/wiki/Routes'

// Lazy load several routes to reduce initial bundle size
const LazyChat = lazy(
    () => import(/* webpackChunkName: "chat" */ 'js/chat/Chat'),
)

const LazyPadEdit = lazy(
    () => import(/* webpackChunkName: "pad-edit" */ 'js/pad/Edit'),
)

const LazyPageEdit = lazy(
    () => import(/* webpackChunkName: "page-edit" */ 'js/page/Edit'),
)

const LazyEventCall = lazy(
    () => import(/* webpackChunkName: "event-call" */ 'js/videoCall/EventCall'),
)
const LazyChatCall = lazy(
    () => import(/* webpackChunkName: "chat-call" */ 'js/videoCall/ChatCall'),
)

const LazyAdminRoutes = lazy(
    () => import(/* webpackChunkName: "admin-routes" */ 'js/admin/Routes'),
)

const LazyGroupRoutes = lazy(
    () => import(/* webpackChunkName: "group-routes" */ 'js/group/Routes'),
)

const LazyUserRoutes = lazy(
    () => import(/* webpackChunkName: "user-routes" */ 'js/user/Routes'),
)

export default function getRootRoutes(site) {
    const { isCustomContentTypeEnabled, customContentTypes } = site || {}
    const safeCustomContentTypes = getSafeCustomContentTypes(customContentTypes)

    return (
        <Route element={<BaseLayout />}>
            <Route
                path="/admin/*"
                element={
                    <Suspense fallback={<div>Loading Admin...</div>}>
                        <LazyAdminRoutes />
                    </Suspense>
                }
            />

            <Route
                path="/videocall/:guid"
                element={
                    <Suspense fallback={<LoadingSpinner />}>
                        <LazyEventCall />
                    </Suspense>
                }
            />
            <Route
                path="/videocall/chat/:guid"
                element={
                    <Suspense fallback={<LoadingSpinner />}>
                        <LazyChatCall />
                    </Suspense>
                }
            />

            <Route
                path="/chat"
                element={
                    <RequireAuth viewer="loggedIn" site="chatEnabled">
                        <Suspense fallback={<LoadingSpinner />}>
                            <LazyChat />
                        </Suspense>
                    </RequireAuth>
                }
            />
            <Route
                path="/chat/:guid"
                element={
                    <RequireAuth viewer="loggedIn" site="chatEnabled">
                        <Suspense fallback={<LoadingSpinner />}>
                            <LazyChat />
                        </Suspense>
                    </RequireAuth>
                }
            />

            <Route path="/:guid/version-history" element={<VersionHistory />} />
            <Route
                path="/:guid/version-history/:revisionGuid"
                element={<VersionHistory />}
            />

            <Route
                path="/news/add"
                element={
                    <RequireAuth
                        viewer="canWriteToContainer"
                        canWriteToContainerSubtype="news"
                    >
                        <NewsAdd />
                    </RequireAuth>
                }
            />
            <Route path="/news/edit/:guid" element={<NewsEdit />} />

            <Route
                path="/blog/add"
                element={
                    <RequireAuth
                        viewer="canWriteToContainer"
                        canWriteToContainerSubtype="blog"
                    >
                        <BlogAdd />
                    </RequireAuth>
                }
            />
            <Route path="/blog/edit/:guid" element={<BlogEdit />} />

            <Route
                path="/podcasts/add"
                element={
                    <RequireAuth
                        viewer="canWriteToContainer"
                        canWriteToContainerSubtype="podcast"
                    >
                        <PodcastAdd />
                    </RequireAuth>
                }
            />
            <Route path="/podcasts/edit/:guid" element={<PodcastEdit />} />
            <Route
                path="/podcasts/:podcastGuid/episodes/add"
                element={<EpisodeAdd />}
            />
            <Route
                path="/podcasts/episodes/edit/:episodeGuid"
                element={<EpisodeEdit />}
            />

            <Route
                path="/questions/add"
                element={
                    <RequireAuth
                        viewer="canWriteToContainer"
                        canWriteToContainerSubtype="question"
                    >
                        <QuestionsAdd />
                    </RequireAuth>
                }
            />
            <Route path="/questions/edit/:guid" element={<QuestionsEdit />} />

            <Route
                path="/discussion/add"
                element={
                    <RequireAuth
                        viewer="canWriteToContainer"
                        canWriteToContainerSubtype="discussion"
                    >
                        <DiscussionsAdd />
                    </RequireAuth>
                }
            />
            <Route
                path="/discussion/edit/:guid"
                element={<DiscussionsEdit />}
            />

            <Route
                path="/events/add"
                element={
                    <RequireAuth
                        viewer="canWriteToContainer"
                        canWriteToContainerSubtype="event"
                    >
                        <EventsAdd />
                    </RequireAuth>
                }
            />
            <Route path="/events/add/:containerGuid" element={<EventsAdd />} />
            <Route path="/events/edit/:guid" element={<EventsEdit />} />

            <Route
                path="/events/view/:guid/:slug/guest-list"
                element={<EventGuestList />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/events/view/:guid/:slug/guest-list"
                element={<EventGuestList />}
            />

            <Route path="/events/edit/:guid" element={<EventsEdit />} />

            <Route
                path="/magazines/add"
                element={
                    <RequireAuth
                        viewer="canWriteToContainer"
                        canWriteToContainerSubtype="magazine"
                    >
                        <MagazineAdd />
                    </RequireAuth>
                }
            />
            <Route path="/magazines/edit/:guid" element={<MagazineEdit />} />

            <Route path="/magazine-issue/*" element={<MagazineIssueRoutes />} />

            <Route path="/pages/add" element={<PageAdd />} />
            <Route
                path="/page/edit/:guid"
                element={
                    <Suspense fallback={<LoadingSpinner />}>
                        <LazyPageEdit />
                    </Suspense>
                }
            />
            <Route
                path="/page/edit/:guid/:slug"
                element={
                    <Suspense fallback={<LoadingSpinner />}>
                        <LazyPageEdit />
                    </Suspense>
                }
            />
            <Route
                path="/admin/site/navigation/footer"
                element={<NavigationFooter />}
            />

            <Route
                path="/wiki/add"
                element={
                    <RequireAuth
                        viewer="canWriteToContainer"
                        canWriteToContainerSubtype="wiki"
                    >
                        <WikiAdd />
                    </RequireAuth>
                }
            />
            <Route path="/wiki/add/:containerGuid" element={<WikiAdd />} />
            <Route path="/wiki/edit/:guid" element={<WikiEdit />} />

            <Route path="/tasks/add" element={<TaskAdd />} />
            <Route path="/tasks/edit/:guid" element={<TaskEdit />} />

            <Route
                path="/polls/add"
                element={
                    <RequireAuth
                        viewer="canWriteToContainer"
                        canWriteToContainerSubtype="poll"
                    >
                        <PollAdd />
                    </RequireAuth>
                }
            />
            <Route path="/polls/edit/:guid" element={<PollEdit />} />

            <Route path="/files/view/:guid" element={<FileView />} />
            <Route path="/files/view/:guid/:slug" element={<FileView />} />

            <Route
                path="/files/edit/:guid"
                element={
                    <Suspense fallback={<LoadingSpinner />}>
                        <LazyPadEdit />
                    </Suspense>
                }
            />

            <Route
                path="/groups/add"
                element={
                    <RequireAuth
                        viewer="canWriteToContainer"
                        canWriteToContainerSubtype="group"
                    >
                        <GroupAdd />
                    </RequireAuth>
                }
            />
            <Route path="/groups/edit/:guid" element={<GroupEdit />} />

            <Route
                path="/groups/view/:groupGuid/:groupSlug/blog/add"
                element={<BlogAdd />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/blog/edit/:guid"
                element={<BlogEdit />}
            />

            {/* Group Podcasts */}
            <Route
                path="/groups/view/:groupGuid/:groupSlug/podcasts/add"
                element={<PodcastAdd />}
            />

            <Route
                path="/groups/view/:groupGuid/:groupSlug/podcasts/edit/:guid"
                element={<PodcastEdit />}
            />

            <Route
                path="/groups/view/:groupGuid/:groupSlug/podcasts/:podcastGuid/episodes/add"
                element={<EpisodeAdd />}
            />

            <Route
                path="/groups/view/:groupGuid/:groupSlug/podcasts/episodes/edit/:episodeGuid"
                element={<EpisodeEdit />}
            />
            {/* End Group Podcasts */}

            <Route
                path="/groups/view/:groupGuid/:groupSlug/news/add"
                element={<NewsAdd />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/news/edit/:guid"
                element={<NewsEdit />}
            />

            <Route
                path="/groups/view/:groupGuid/:groupSlug/questions/add"
                element={<QuestionsAdd />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/questions/edit/:guid"
                element={<QuestionsEdit />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/discussion/add"
                element={<DiscussionsAdd />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/discussion/edit/:guid"
                element={<DiscussionsEdit />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/events/add"
                element={<EventsAdd />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/events/add/:containerGuid"
                element={<EventsAdd />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/events/edit/:guid"
                element={<EventsEdit />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/pad/edit/:guid"
                element={
                    <Suspense fallback={<LoadingSpinner />}>
                        <LazyPadEdit />
                    </Suspense>
                }
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/wiki/add"
                element={<WikiAdd />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/wiki/add/:containerGuid"
                element={<WikiAdd />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/wiki/edit/:guid"
                element={<WikiEdit />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/tasks/add"
                element={<TaskAdd />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/tasks/edit/:guid"
                element={<TaskEdit />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/pages/add"
                element={<PageAdd />}
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/page/edit/:guid"
                element={
                    <Suspense fallback={<LoadingSpinner />}>
                        <LazyPageEdit />
                    </Suspense>
                }
            />
            <Route
                path="/groups/view/:groupGuid/:groupSlug/page/edit/:guid/:slug"
                element={
                    <Suspense fallback={<LoadingSpinner />}>
                        <LazyPageEdit />
                    </Suspense>
                }
            />

            {safeCustomContentTypes?.map(({ key, name, slug }) => (
                <Fragment key={key}>
                    {isCustomContentTypeEnabled && (
                        <>
                            <Route
                                path={`/${slug}/add`}
                                element={
                                    <RequireAuth
                                        viewer="canWriteToContainer"
                                        canWriteToContainerSubtype={key}
                                    >
                                        <CustomContentCreate
                                            subtype={key}
                                            name={name.toLowerCase()}
                                        />
                                    </RequireAuth>
                                }
                            />
                            <Route
                                path={`/groups/view/:groupGuid/:groupSlug/${slug}/add`}
                                element={
                                    <RequireAuth
                                        viewer="canWriteToContainer"
                                        canWriteToContainerSubtype={key}
                                    >
                                        <CustomContentCreate
                                            subtype={key}
                                            name={name.toLowerCase()}
                                        />
                                    </RequireAuth>
                                }
                            />
                        </>
                    )}
                    <Route
                        path={`/${slug}/edit/:guid`}
                        element={<CustomContentEdit subtype={key} />}
                    />
                    <Route
                        path={`/groups/view/:groupGuid/:groupSlug/${slug}/edit/:guid`}
                        element={<CustomContentEdit subtype={key} />}
                    />
                </Fragment>
            ))}

            <Route element={<Layout />}>
                <Route path="/news/*" element={<NewsRoutes />} />
                <Route path="/blog/*" element={<BlogRoutes />} />
                <Route path="/podcasts/*" element={<PodcastRoutes />} />
                <Route path="/questions/*" element={<QuestionsRoutes />} />
                <Route path="/discussion/*" element={<DiscussionsRoutes />} />
                <Route path="/events/*" element={<EventsRoutes />} />
                <Route path="/magazines/*" element={<MagazinesRoutes />} />
                <Route path="/magazine/*" element={<MagazineRoutes />} />
                <Route
                    path="/groups/*"
                    element={
                        <Suspense fallback={<LoadingSpinner />}>
                            <LazyGroupRoutes />
                        </Suspense>
                    }
                />
                <Route path="/cms/*" element={<LegacyCmsPageRedirect />} />
                <Route path="/page/*" element={<PageRoutes />} />
                <Route path="/wiki/*" element={<WikiRoutes />} />
                <Route path="/polls/*" element={<PollsRoutes />} />
                <Route
                    path="/user/:userGuid/*"
                    element={
                        <Suspense fallback={<LoadingSpinner />}>
                            <LazyUserRoutes />
                        </Suspense>
                    }
                />
                <Route path="/search/*" element={<SearchRoutes />} />
                <Route
                    path="/members"
                    element={<Navigate to="/users" replace />}
                />
                <Route path="/users/*" element={<UsersRoutes />} />
                {safeCustomContentTypes?.map(({ key, slug }) => (
                    <Route
                        key={`${key}-item`}
                        path={`/${slug}/view/:guid/:titleSlug`}
                        element={<CustomContentDetail />}
                    />
                ))}
                <Route path="/*" element={<CoreRoutes />} />
            </Route>
        </Route>
    )
}
